// /app/frontend/src/pages/seo/VerifiedDatingAppPage.jsx
// Focus: Trust deep-dive on how Stripe Identity + moderation actually work.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  mobileAppSchema,
  buildFaqSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FeatureCard,
  StepCard,
  FaqList,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

const FAQS = [
  {
    q: 'How does identity verification work?',
    a: 'Verification is processed through Stripe Identity, a trusted platform used by major businesses worldwide. You take a photo of your government-issued ID and a quick selfie, and Stripe matches them in seconds. Your verified status is recorded on your UnityLove profile.',
  },
  {
    q: 'Does UnityLove store my ID?',
    a: 'No. UnityLove does not store copies of your identification documents on our servers. ID handling is performed by Stripe Identity. UnityLove only receives the verification result — confirmation that you are a real, of-age person.',
  },
  {
    q: 'Is verification required to use UnityLove?',
    a: 'You can create a profile without verifying. Some features — particularly After Dark video lounges, speed dating, and certain higher-trust community spaces — require verification. The verified badge on your profile also helps you stand out and signals authenticity to other members.',
  },
  {
    q: 'What if my verification fails?',
    a: 'Common issues are blurry photos, mismatched faces, or expired IDs. You can retry directly from your profile settings. If you continue to have trouble, our support team can help, and Stripe Identity also provides direct assistance.',
  },
  {
    q: 'How does UnityLove protect against catfishing?',
    a: 'Three layers: identity verification (the strongest single defense), community reporting tools on every profile and message, and an active moderation team that reviews reports and removes bad actors. Catfishing on a verified-only platform is significantly harder than on apps where anyone can sign up with stock photos.',
  },
];

export default function VerifiedDatingAppPage() {
  const path = '/verified-dating-app';
  const schemas = [
    organizationSchema,
    mobileAppSchema,
    buildFaqSchema(FAQS),
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'Verified Dating App', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Verified Dating App For Safer Connections"
        description="Video verification, authentic profiles, and community-focused features help create safer online connections."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="A UnityLove Standard"
        headline="Authentic Connections Start With Verification"
        sub="A dating platform built around the radical idea that everyone messaging you is actually a real person, who they say they are, and old enough to be there."
        body="Verification is securely handled through Stripe Identity. UnityLove does not store your ID documents."
        primaryCta={{ label: 'Create Your Profile Free', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'How Verification Works', to: '/verify-age' }}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {/* Why verification matters */}
        <section className="mb-12">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-4">
            Why Verification Matters
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              The single biggest reason dating goes wrong online is not chemistry or compatibility — it is that the person on the other end is not who they said they were. Stolen photos. Fake names. Bots running scripts. Members who would never pass an in-person sanity check.
            </p>
            <p>
              Identity verification ends that game. When members go through Stripe Identity to confirm their ID and selfie, the catfishing, stolen-photo, and bot-account economy that haunts mainstream apps largely cannot operate.
            </p>
          </div>
        </section>

        {/* What verification gets you */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6 text-center">
            What Verification Protects
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon="🚫"
              accent="rose"
              title="Anti-Catfish"
              body="Every verification helps ensure members are who they claim to be, reducing fake profiles and stolen photos. The same person cannot easily build twenty profiles with twenty borrowed identities."
            />
            <FeatureCard
              icon="🤖"
              accent="violet"
              title="Anti-Bot"
              body="Automated accounts and spam networks cannot easily pass biometric identity verification. The economic incentive for botting verified platforms collapses."
            />
            <FeatureCard
              icon="🔞"
              accent="amber"
              title="Age Verification"
              body="Underage users cannot pass verification. Adult features, video lounges, and higher-trust spaces stay genuinely adults-only."
            />
            <FeatureCard
              icon="🛡️"
              accent="emerald"
              title="Reduced Fetishization"
              body="Fetish accounts often rely on anonymity. Verification, paired with strict moderation, makes targeted harassment harder to perpetrate and easier to act on when reported."
            />
            <FeatureCard
              icon="🔒"
              accent="sky"
              title="Privacy First"
              body="UnityLove never sees your ID. All ID handling is done by Stripe Identity, a trusted, regulated platform used by major businesses worldwide."
            />
            <FeatureCard
              icon="✅"
              accent="emerald"
              title="Community Trust"
              body="A verified badge tells everyone messaging you that you are real. Profiles with verified badges receive more thoughtful messages, more replies, and more genuine connection."
            />
          </div>
        </section>

        {/* The 4-step process */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6">
            The Four-Step Process
          </h2>
          <div className="grid gap-3">
            <StepCard n={1} title="Take a photo of your government-issued ID" body="Driver license, passport, or other state-issued ID. The photo stays with Stripe Identity, not UnityLove." />
            <StepCard n={2} title="Take a quick selfie" body="A second photo for face-matching. This confirms the person holding the ID is you." />
            <StepCard n={3} title="Stripe Identity securely verifies the match" body="Match results return in seconds. The platform handling this is regulated and used by companies like Lyft, Shopify, and DoorDash." />
            <StepCard n={4} title="Your profile receives verified status" body="A visible badge on your profile signals authenticity. You unlock additional UnityLove features and the trust of every member you message." />
          </div>
        </section>

        {/* Beyond verification */}
        <section className="mb-14 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            Beyond Verification: Layers Of Safety
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4">
            Identity verification is the foundation, but safety on UnityLove sits on a stack of layers:
          </p>
          <ul className="space-y-3 text-slate-700">
            <li><strong className="text-slate-900">Profile reporting.</strong> Every profile and message can be reported. Reports go to an active moderation team, not a queue that nobody reads.</li>
            <li><strong className="text-slate-900">Community standards.</strong> Explicit policies addressing fetishization, harassment, racism, and predatory behavior — enforced consistently.</li>
            <li><strong className="text-slate-900">Block tools.</strong> One-tap blocking, with no retaliation pathway. The block is invisible to the blocked user.</li>
            <li><strong className="text-slate-900">Privacy controls.</strong> Granular settings for who sees your profile, your photos, and your location data.</li>
            <li><strong className="text-slate-900">Video lounges.</strong> Move to video early so the people you meet are real to you before any in-person plans.</li>
          </ul>
        </section>

        {/* FAQ */}
        <section className="mb-4">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-2">
            Frequently Asked Questions
          </h2>
          <p className="text-slate-600 mb-6">
            Quick answers to the questions members ask most about verification.
          </p>
          <FaqList items={FAQS} />
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
